import {
  Card,
  CardHeader,
  Tabs,
  TabsList,
  TabsTrigger,
  Typography,
} from '@growdash/design-system/atoms';
import { DataTable } from '@growdash/design-system/molecules/index.js';
import { useGrowintelService, useTabState } from '@growdash/growintel/hooks';
import { useQuery } from '@tanstack/react-query';
import React from 'react';
import { PaginatedResponseMenuItem } from '../../../generated';
import { searchTabs } from '../tabs';

import columns from './columns';
import ItemFilters from './filters';

export default function SearchItemView() {
  const service = useGrowintelService();
  const { filters } = useTabState('search');
  const { item: itemFilters } = filters;
  const { name, area, city, cuisine } = itemFilters;

  const [pageIndex, setPageIndex] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(20);

  const { activeSubTab, updateTabState } = useTabState('search');

  const enabled = name.length >= 3 || !!city || !!area || !!cuisine;
  const nameValue = !!name.length ? name : undefined;
  const cityValue = !!city ? city : undefined;
  const areaValue = !!area ? area : undefined;
  const cuisineValue = !!cuisine ? cuisine : undefined;
  const { data, isFetching, isError } = useQuery({
    queryKey: [
      'search-item',
      nameValue,
      cityValue,
      areaValue,
      cuisineValue,
      pageIndex,
      pageSize,
    ],
    queryFn: async (): Promise<PaginatedResponseMenuItem | undefined> => {
      try {
        const nameValue = !!name.length ? name : undefined;
        const response =
          await service?.menuItemsAPI.searchMenuItemsMenuItemsSearchGet(
            nameValue,
            cityValue,
            areaValue,
            cuisineValue,
            pageIndex + 1,
            pageSize,
          );
        return response?.data;
      } catch (err) {
        throw err;
      }
    },
    enabled,
    retry: false,
  });

  React.useEffect(() => {
    setPageIndex(0);
  }, [name, pageSize]);

  // Views
  const FiltersView = () => (
    <CardHeader className="flex items-end gap-4 px-4 bg-quarternary rounded-t-[inherit]">
      <div className="flex items-center gap-4">
        <Typography className="inline text-sm text-nowrap text-neutral font-medium">
          Search by
        </Typography>
        <Tabs
          onValueChange={(tab) => {
            updateTabState({
              selectedSubTab: tab,
            });
          }}
          value={activeSubTab}
        >
          <TabsList>
            {searchTabs.map((t) => {
              return (
                <TabsTrigger key={t.value} value={t.value} className="px-4">
                  {t.label}
                </TabsTrigger>
              );
            })}
          </TabsList>
        </Tabs>
      </div>
      <ItemFilters />
    </CardHeader>
  );

  const DataView = () =>
    !isFetching && data === undefined ? null : (
      <div className="w-full flex justify-between gap-2 animate-fade-in">
        <div className="basis-full">
          <Card className="p-4 border-none shadow-none">
            <DataTable
              isBorderActive={false}
              isServerSide
              pagination={{
                pageCount: data?.total_pages ?? 0,
                pageIndex: pageIndex,
                pageSize: pageSize,
                onPageChange: (pageIndex) => {
                  setPageIndex(pageIndex);
                },
                onRowsPerPageChange: (take: number) => {
                  setPageSize(take);
                },
              }}
              isPaginationActive
              columns={columns}
              data={data?.items ?? []}
              isLoading={isFetching}
            />
          </Card>
        </div>
      </div>
    );

  const ErrorView = () => (
    <div className="w-full flex justify-center items-center h-full py-12 animate-fade-in">
      <p className="text-destructive">
        An error occurred while fetching the data. Please try again.
      </p>
    </div>
  );

  const EmptyStateView = () => (
    <div className="w-full flex justify-center items-center h-full py-12 animate-fade-in">
      <p className="text-muted-foreground">
        Please enter a search term or apply at least one filter: cuisine, city,
        or area.
      </p>
    </div>
  );

  return (
    <>
      <FiltersView />
      {!isError && !data && !isFetching && <EmptyStateView />}
      {isError && <ErrorView />}
      {!isError && <DataView />}
    </>
  );
}
